"use client";

import { useTranslation } from "react-i18next";
import { useEffect, useState, useRef, useMemo } from "react";
import Spinner from "./Spinner";
import { useAuth } from "@/context/AuthContext";
import { useLanguages } from "@/customHooks/useLanguages";
import { getLanguageDisplayLabel } from "@/utils/languageDisplay";
import { useTrainingLanguageStore } from "@/store/trainingLanguageStore";

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [currentLocale, setCurrentLocale] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const { availableLanguages, isLoading } = useLanguages();
  // Training filter: when set, restrict options to event/group languages
  const allowedLocaleCodes = useTrainingLanguageStore(
    (s) => s.allowedLocaleCodes,
  );

  // Filter to allowed locales when training has a scenario with group; else show all
  const displayLanguages = useMemo(() => {
    if (!allowedLocaleCodes || allowedLocaleCodes.length === 0)
      return availableLanguages;
    const set = new Set(allowedLocaleCodes);
    return availableLanguages.filter((lang) => set.has(lang.locale_code));
  }, [availableLanguages, allowedLocaleCodes]);

  // Apply the user's own language. If the restriction lists no longer offer it, it is still THEIR
  // language and every locale ships with the app, so it is applied anyway — the list governs what
  // may be PICKED, not what someone already has. Silently switching to availableLanguages[0]
  // instead meant a Dutch user whose client had been restricted was shown German or English (the
  // list is ordered by locale_code), with nothing anywhere saying their setting had been ignored.
  // Only when the user or their language really changes — a reloaded copy of the same user
  // (login renewal, settings refresh) must not overrule what the learner picked meanwhile.
  const appliedUserLanguageRef = useRef<string | null>(null);
  useEffect(() => {
    if (!user?.language || availableLanguages.length === 0) return;
    const applied = `${user.id}:${user.language}`;
    if (appliedUserLanguageRef.current === applied) return;
    appliedUserLanguageRef.current = applied;

    setCurrentLocale(user.language);
    i18n.changeLanguage(user.language);
    localStorage.setItem("i18nextLng", user.language);
  }, [user, availableLanguages, i18n]);

  // While a TALK is open the conversation must run in a language its Event actually supports, so
  // here — unlike the preference above — switching is the point. What changes is WHICH language:
  // the user's own when the Event permits it, and otherwise the first in locale order rather than
  // whatever order the API happened to return, so the same Event always resolves the same way.
  useEffect(() => {
    if (
      !allowedLocaleCodes ||
      allowedLocaleCodes.length === 0 ||
      displayLanguages.length === 0
    ) {
      return;
    }
    if (allowedLocaleCodes.includes(i18n.language || "")) return;

    const preferred =
      user?.language && allowedLocaleCodes.includes(user.language)
        ? user.language
        : [...displayLanguages]
            .map((lang) => lang.locale_code)
            .sort()[0];

    i18n.changeLanguage(preferred);
    setCurrentLocale(preferred);
    localStorage.setItem("i18nextLng", preferred);
  }, [allowedLocaleCodes, displayLanguages, i18n, user]);

  useEffect(() => {
    if (i18n.isInitialized) {
      const storedLocale = localStorage.getItem("i18nextLng");
      setCurrentLocale(storedLocale || i18n.language);
      setMounted(true);
    } else {
      const handleInit = () => {
        const storedLocale = localStorage.getItem("i18nextLng");
        setCurrentLocale(storedLocale || i18n.language);
        setMounted(true);
      };
      i18n.on("initialized", handleInit);
      return () => i18n.off("initialized", handleInit);
    }
  }, [i18n]);

  const handleLanguageChange = (localeCode: string) => {
    i18n.changeLanguage(localeCode);
    setCurrentLocale(localeCode);
    setDropdownOpen(false);
    localStorage.setItem("i18nextLng", localeCode);
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(e.target as Node)
    ) {
      setDropdownOpen(false);
    }
  };

  useEffect(() => {
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  if (!mounted) return null;

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        onClick={() => setDropdownOpen((prev) => !prev)}
        className="px-3 py-2 border border-gray-300 rounded-[6px] text-sm font-medium hover:bg-gray-100 transition-colors cursor-pointer"
        aria-haspopup="true"
        aria-expanded={dropdownOpen}
        data-testid="language-switcher"
      >
        {isLoading ? (
          <Spinner customDimension="h-4 w-4" />
        ) : (
          (() => {
            const currentLang = displayLanguages.find(
              (l) => l.locale_code === currentLocale,
            );
            return currentLang
              ? getLanguageDisplayLabel(currentLang, displayLanguages)
              : currentLocale || "";
          })()
        )}
      </button>

      {dropdownOpen && (
        <div className="absolute right-0 mt-2 w-36 bg-white border border-gray-200 rounded-[6px]  shadow-lg z-[60] max-h-[500px] overflow-y-auto">
          {displayLanguages.map((lang) => (
            <div
              key={lang.locale_code}
              onClick={() => handleLanguageChange(lang.locale_code)}
              data-testid={`language-option-${lang.locale_code}`}
              className={`px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 ${
                lang.locale_code === currentLocale
                  ? "bg-gray-50 font-semibold"
                  : ""
              }`}
            >
              {getLanguageDisplayLabel(lang, displayLanguages)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
